import { Component, OnDestroy, OnInit } from '@angular/core';
import { interval, Subject, Subscription, takeUntil, takeWhile } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';

import { SigningService } from './signing.service';
import { AppService, UserService } from '../../../../shared/service/user.service';
import { Severity, ToastService } from '../../../../shared/components/toast/toast.service';
import { AppSubject } from '../../../../shared/app-subject';
import { validateAllFormFields } from '../../../../shared/components/validator/validator.service';
import { InvoiceService } from '../../../customer/invoice/invoice.service';
import { AgreementSigningHandlerService } from './AgreementSigningHandler.service';

@Component({
  selector: 'app-signing',
  templateUrl: './signing.component.html'
})
export class SigningComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  submitting = false;
  private isInternalClosing = false;

  items: Array<any> = [];
  selectedCTS: any;
  pluginNotFound = false;
  isTypeSelectionVisible = false;
  isCtsSelectionVisible = false;
  isMysignWaitVisible = false;
  isSigningProcessVisible = false;
  isBatchSigningViewVisible = false;
  displayPinCode = false;
  signingTypes: Array<any> = [];
  ctsList: any[] = [];
  batchSigningInvoices: any[] = [];
  batchSigningWorkEffortId = '';

  selectedType: any;
  selectedTypeReason = '';
  signingData = {
    partyOtherIndentId: '',
    idValue: '',
    idValues: '',
    partyClassificationId: '',
    signingType: '',
    pinCode: '',
    referenceCode: '',
    agreementContentId: '',
  };

  form = this.fb.group({
    pinCode: ['', Validators.required],
  });

  timeLeft = 180; // 3 minutes in seconds
  minutes = 3;
  seconds = 0;
  isLoading = true;

  constructor(
    private signingService: SigningService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private invoiceService: InvoiceService,
    private agreementSigningHandler: AgreementSigningHandlerService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.resetSigningState();
    this.initializeSigningTypes();
    this.subscribeToServiceEvents();
    setTimeout(() => {
      if (this.canShowSigningDialogs()) {
        this.signingService.promptResumeBatchSigningAttempt();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetSigningState(): void {
    UserService.set(UserService.KEY.SIGNING, '');
    UserService.set(UserService.KEY.REMOTE_SIGNING, '');
    UserService.set('RECORD_SIGNING_TYPES', '');
    this.isTypeSelectionVisible = false;
    this.isCtsSelectionVisible = false;
    this.isMysignWaitVisible = false;
    this.isSigningProcessVisible = false;
    this.pluginNotFound = false;
    this.displayPinCode = false;
    this.submitting = false;
    this.isInternalClosing = false;
    this.form.reset();
    this.selectedType = null;
    this.selectedTypeReason = '';
    this.timeLeft = 180;
    this.minutes = 3;
    this.seconds = 0;
  }

  cancel(): void {
    if (this.isInternalClosing) {
      this.isInternalClosing = false;
      return;
    }
    this.signingService.cancel();
    this.resetSigningState();
  }

  private initializeSigningTypes(): void {
    const signingTypesRaw = UserService.get('RECORD_SIGNING_TYPES') || UserService.get(UserService.KEY.SIGNING_TYPES);
    if (signingTypesRaw && signingTypesRaw !== 'NULL') {
      try {
        const arr = JSON.parse(signingTypesRaw);
        this.signingTypes = arr.map((item: any) => ({
          value: item.partyClassificationId,
          label: item.description || item.classificationDesc,
          disabled: item.available === 'N',
          unavailableReason: item.unavailableReason,
          certList: item.certList
        }));
        const firstAvailable = this.signingTypes.find(item => !item.disabled);
        this.selectedType = firstAvailable?.value || this.signingTypes[0]?.value;
        this.updateSelectedTypeReason();
      } catch (e) {
        console.error('Error parsing SIGNING_TYPES', e);
        this.signingTypes = [];
        this.selectedType = null;
        this.selectedTypeReason = '';
      }
    } else {
      this.signingTypes = [];
      this.selectedType = null;
      this.selectedTypeReason = '';
    }
  }

  private subscribeToServiceEvents(): void {
    this.signingService.errorSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((error: any) => {
        if (error && (error.status === 0 || error?.name === 'TimeoutError')) {
          this.pluginNotFound = true;
        }
      });

    this.signingService.displaySubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => this.handleDisplayChange(res));

    this.signingService.batchSigningViewSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: any) => {
        if (!this.canShowSigningDialogs()) {
          this.closeSigningDialogs();
          return;
        }
        this.batchSigningInvoices = response?.items || [];
        this.batchSigningWorkEffortId = response?.workEffortId || '';
        this.isBatchSigningViewVisible = true;
      });

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationStart && event.url.startsWith('/login')) {
          this.closeSigningDialogs();
        }
      });
  }

  private handleDisplayChange(res: string): void {
    switch (res) {
      case 'form1':
        this.initializeSigningTypes();
        this.isTypeSelectionVisible = true;
        this.pluginNotFound = false;
        break;
      case 'form2':
        this.setupCtsSelection();
        this.isInternalClosing = true;
        this.isTypeSelectionVisible = false;
        this.isCtsSelectionVisible = true;
        break;
      case 'form3':
        this.isSigningProcessVisible = true;
        break;
      case 'closeForm3':
        this.isSigningProcessVisible = false;
        break;
    }
  }

  private setupCtsSelection(): void {
    const remoteSigningRaw = UserService.get('REMOTE_SIGNING');
    if (remoteSigningRaw) {
      try {
        const arr = JSON.parse(remoteSigningRaw);
        this.ctsList = arr;
        if (arr && arr.length > 0) {
          this.selectedCTS = arr[0].partyOtherIndentId;
          this.items = arr.map((item: any) => ({
            value: item.partyOtherIndentId,
            label: item.idValue
          }));
          this.displayPinCode = arr[0]?.requirePinCode === 'Y';
        }
      } catch (e) {
        console.error('Error parsing REMOTE_SIGNING', e);
      }
    }
  }

  submit(): void {
    if (this.submitting) return;

    if (!this.selectedType && this.signingTypes.length > 0) {
      this.selectedType = this.signingTypes.find(item => !item.disabled)?.value || this.signingTypes[0].value;
    }
    const selected = this.signingTypes.find(item => item.value === this.selectedType);
    if (selected?.disabled) {
      this.toastService.setMessage({
        severity: Severity.ERROR,
        detail: selected.unavailableReason || 'Hình thức ký này chưa khả dụng'
      });
      return;
    }
    UserService.set(UserService.KEY.SIGNING, this.selectedType);
    this.signingData.partyClassificationId = this.selectedType;

    const runValue = JSON.parse(UserService.get('RUN_VALUE'));
    this.signingService.run(runValue).then(() => {
      this.submitting = false;
    }).catch(() => {
      this.submitting = false;
    });

    this.isInternalClosing = true;
    this.isTypeSelectionVisible = false;
  }

  submit2(): void {
    if (this.submitting) return;

    if (this.displayPinCode && !validateAllFormFields(this.form)) {
      return;
    }

    this.submitting = true;
    this.isInternalClosing = true;
    this.isCtsSelectionVisible = false;
    this.populateSigningData();

    if (this.displayPinCode) {
      this.isSigningProcessVisible = true;
    } else {
      this.isMysignWaitVisible = true;
      this.startTimer();
    }

    this.signingService.remoteSigning(this.signingData).subscribe({
      next: () => {
        this.submitting = false;
        this.handleSigningSuccess();
      },
      error: (error) => {
        this.submitting = false;
        this.handleSigningError(error);
      }
    });
  }

  private populateSigningData(): void {
    const runValue = JSON.parse(UserService.get('RUN_VALUE'));
    this.signingData.pinCode = this.displayPinCode ? (this.form.value.pinCode || '') : '';
    this.signingData.partyOtherIndentId = typeof this.selectedCTS === 'object' ? this.selectedCTS.value : this.selectedCTS;
    this.signingData.idValue = runValue?.idValue;
    this.signingData.idValues = runValue?.idValues;
    this.signingData.signingType = runValue?.actionType;
    this.signingData.referenceCode = runValue?.referenceCode || '';
    this.signingData.agreementContentId = runValue?.agreementContentId || '';
  }

  private handleSigningSuccess(): void {
    const shouldReloadGlobalSigningData = !this.signingService.isCurrentPdfRecordSigning();
    this.toastService.success();
    this.resetSigningState();
    if (shouldReloadGlobalSigningData) {
      AppSubject.setReloadData('success');
    }
  }

  private handleSigningError(error: any): void {
    this.toastService.setMessage({ severity: Severity.ERROR, detail: this.getErrorMessage(error) });
    this.resetSigningState();
    AppSubject.setReloadData('error');
  }

  onCTSChange(selectedValue: any): void {
    const id = typeof selectedValue === 'object' ? selectedValue.value : selectedValue;
    this.selectedCTS = id;
    const selected = this.ctsList.find(item => item.partyOtherIndentId === id);
    this.displayPinCode = selected?.requirePinCode === 'Y';
  }

  typeSelect(event: any): void {
    this.selectedType = event.value;
    this.updateSelectedTypeReason();
  }

  private updateSelectedTypeReason(): void {
    const selected = this.signingTypes.find(item => item.value === this.selectedType);
    this.selectedTypeReason = selected?.disabled ? (selected.unavailableReason || 'Hình thức ký này chưa khả dụng') : '';
  }

  private getErrorMessage(error: any): string {
    const body = error?.error || error;
    const raw = body?.errorMessage || body?.errors || body?.message || body?.statusText;
    if (Array.isArray(raw)) {
      return raw.join('\n');
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw;
    }
    return 'Không thể ký biên bản. Vui lòng thử lại hoặc liên hệ quản trị hệ thống.';
  }

  getInvoiceField(invoice: any, fields: string[]): any {
    const field = fields.find(item => invoice && invoice[item] !== undefined && invoice[item] !== null);
    return field ? invoice[field] : '';
  }

  getBatchSigningInvoiceSerial(invoice: any): any {
    const invoiceForm = this.getInvoiceField(invoice, ['invoiceForm', 'form', 'templateCode']);
    const invoiceSerial = this.getInvoiceField(invoice, ['invoiceSerial', 'serial']);
    if (invoiceForm || invoiceSerial) {
      return `${invoiceForm || ''}${invoiceSerial || ''}`;
    }
    return this.getInvoiceField(invoice, ['invoiceFormSerial']);
  }

  private canShowSigningDialogs(): boolean {
    return !!AppService.get(AppService.KEY.MOQUI_SESSION_TOKEN) && !this.router.url.startsWith('/login');
  }

  private closeSigningDialogs(): void {
    this.pluginNotFound = false;
    this.isTypeSelectionVisible = false;
    this.isCtsSelectionVisible = false;
    this.isMysignWaitVisible = false;
    this.isSigningProcessVisible = false;
    this.isBatchSigningViewVisible = false;
    this.signingService.closeResumeBatchSigningPrompt();
  }

  private startTimer(): void {
    this.timeLeft = 180;
    this.minutes = 3;
    this.seconds = 0;
    this.isLoading = true;

    interval(1000)
      .pipe(
        takeWhile(() => this.timeLeft > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.timeLeft--;
        this.minutes = Math.floor(this.timeLeft / 60);
        this.seconds = this.timeLeft % 60;
        if (this.timeLeft === 0) {
          this.isLoading = false;
        }
      });
  }

  // Giữ nguyên function tương tác với AgreementSigningHandlerService theo logic cũ
  downloadSignTool(version: 'x64' | 'x86'): void {
    if (version === 'x86') {
      this.agreementSigningHandler.downloadSignTool32bit();
    } else {
      this.agreementSigningHandler.downloadSignTool();
    }
  }
}
